import { PIPELINE_STEPS } from './matterPipeline.js';
import { DEBTOR_INSTALLMENT_PLANS } from './installmentPlans.js';
import { formatDemandId, noteDemandIdAllocated } from './demandId.js';

const FIRST_NAMES = [
  'Alex',
  'Jordan',
  'Casey',
  'Riley',
  'Morgan',
  'Taylor',
  'Avery',
  'Quinn',
  'Harper',
  'Reese',
  'Cameron',
  'Drew',
  'Skyler',
  'Parker',
  'Jamie',
  'Blake',
  'Finley',
  'Rowan',
  'Sawyer',
  'Emerson',
  'Kai',
  'Logan',
  'Peyton',
  'River',
  'Sam',
];

const LAST_NAMES = [
  'Nguyen',
  'Patel',
  'Brooks',
  'Garcia',
  'Chen',
  'Walsh',
  'Okoye',
  'Singh',
  'Martinez',
  'Kim',
  'Foster',
  'Reed',
  'Bennett',
  'Diaz',
  'Hughes',
  'Coleman',
  'Price',
  'Hayes',
  'Turner',
  'Bailey',
  'Myers',
  'Ford',
  'Griffin',
  'West',
  'Bishop',
];

const COMPANIES = [
  'Summit Freight Co.',
  'Blue Harbor Retail',
  'Cedar Grove Labs',
  'Iron Gate Fabrication',
  'Lakeview Dental Group',
  'Pioneer Softworks',
  'Redwood Supply Chain',
  'Atlas Mechanical',
  'Brightline Media',
  'Northstar Logistics',
  'Copperfield Trading',
  'Evergreen HVAC',
  'Beacon Staffing LLC',
  'Horizon Print & Pack',
  'Silverline Construction',
  'Prairie Wind Energy',
  'Coastal Parts Depot',
  'Maple & Oak Interiors',
  'Keystone Accounting',
  'VoltEdge Systems',
  'Granite Peak Outfitters',
  'Harborline Marine',
  'Sunfield Agritech',
  'MetroFleet Services',
  'Twin Rivers Foods',
];

const STATES = [
  'CA',
  'TX',
  'NY',
  'FL',
  'IL',
  'WA',
  'CO',
  'GA',
  'AZ',
  'NC',
  'OH',
  'PA',
  'MA',
  'NJ',
  'OR',
];

const CITIES = {
  CA: 'Los Angeles',
  TX: 'Austin',
  NY: 'Buffalo',
  FL: 'Tampa',
  IL: 'Chicago',
  WA: 'Seattle',
  CO: 'Denver',
  GA: 'Atlanta',
  AZ: 'Phoenix',
  NC: 'Charlotte',
  OH: 'Columbus',
  PA: 'Philadelphia',
  MA: 'Boston',
  NJ: 'Newark',
  OR: 'Portland',
};

const STREETS = [
  '1200 Market St',
  '88 Harbor Ave',
  '450 Industrial Blvd',
  '17 Pine Ridge Rd',
  '902 Commerce Way',
  '310 Oak Lawn Dr',
  '55 Riverfront Pl',
  '7800 Tech Park',
  '214 Cedar Ln',
  '66 Broadway',
];

const ATTORNEYS = [
  { name: 'Jordan Hale, Esq.', email: 'jordan.hale@partnerlaw.demo' },
  { name: 'Morgan Cole, Esq.', email: 'morgan.cole@partnerlaw.demo' },
  { name: 'Avery Quinn, Esq.', email: 'avery.quinn@partnerlaw.demo' },
  { name: 'Riley West, Esq.', email: 'riley.west@partnerlaw.demo' },
  { name: 'Cameron Brooks, Esq.', email: 'cameron.brooks@partnerlaw.demo' },
];

const DISPUTES = [
  'CORPORATE_BUSINESS_DEBT',
  'EMPLOYMENT_LABOR',
  'FAMILY_DOMESTIC',
  'INTELLECTUAL_PROPERTY',
  'PERSONAL_INJURY',
  'REAL_ESTATE',
  'MARITIME_ADMIRALTY',
  'BANKING_FINANCE',
  'CIVIL_RIGHTS',
  'HEALTHCARE',
];

const STAGE_IDS = PIPELINE_STEPS.map((s) => s.id);

/** Offline demo volume for load/perf checks across list + kanban views. */
export const DEMO_SEED_COUNT = 1000;

/** Offline showcase rows used when the API is unavailable. */
export function isDemoSeedMatter(matter) {
  const id = String(matter?.ROWID || '');
  if (id.startsWith('m_demo_')) return true;
  return String(matter?.Entry_Tag || '').toLowerCase() === 'demo';
}

export function countBillableActiveMatters(matters = []) {
  return (matters || []).filter((matter) => !isDemoSeedMatter(matter)).length;
}

/** Spread demands across all pipeline stages (roughly even). */
function stageForIndex(index, total = DEMO_SEED_COUNT) {
  const base = Math.floor(total / STAGE_IDS.length);
  const extra = total % STAGE_IDS.length;
  let cursor = 0;
  for (let i = 0; i < STAGE_IDS.length; i += 1) {
    const count = base + (i < extra ? 1 : 0);
    if (index < cursor + count) return STAGE_IDS[i];
    cursor += count;
  }
  return STAGE_IDS[STAGE_IDS.length - 1];
}

function isoDaysAgo(days, hour = 12) {
  const d = new Date(Date.UTC(2026, 7, 20, hour, 0, 0));
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function trackingFor(i) {
  const raw = String(920000000000000000 + i * 137).padStart(22, '0');
  return `${raw.slice(0, 4)} ${raw.slice(4, 8)} ${raw.slice(8, 12)} ${raw.slice(12, 16)} ${raw.slice(16, 20)} ${raw.slice(20)}`;
}

function buildMatter(i) {
  const n = i + 1;
  const status = stageForIndex(i);
  const statusIndex = STAGE_IDS.indexOf(status);
  const first = FIRST_NAMES[i % FIRST_NAMES.length];
  const last = LAST_NAMES[(i * 3) % LAST_NAMES.length];
  const company = COMPANIES[i % COMPANIES.length];
  const state = STATES[i % STATES.length];
  const attorney =
    statusIndex >= 1 ? ATTORNEYS[i % ATTORNEYS.length] : { name: null, email: null };
  const origin = i % 3 === 0 ? 'AUTOMATED' : 'MANUAL';
  const plan = DEBTOR_INSTALLMENT_PLANS[i % DEBTOR_INSTALLMENT_PLANS.length];
  const amount = Math.round((2500 + ((i * 1739) % 42000) + (i % 7) * 125.5) * 100) / 100;
  // Spread created dates over ~4 months so larger seeds stay in the past.
  const createdDays = 1 + ((i * 3) % 120);
  const updatedDays = Math.max(0, createdDays - statusIndex * 3 - (i % 4));
  const dispute = DISPUTES[i % DISPUTES.length];
  const id = `m_demo_${2000 + n}`;
  const demandId = formatDemandId(2026, n);
  noteDemandIdAllocated(demandId);

  return {
    ROWID: id,
    Demand_ID: demandId,
    Debtor_Name: `${first} ${last}`,
    Debtor_Company: company,
    Debtor_Street: STREETS[i % STREETS.length],
    Debtor_City: CITIES[state] || 'Springfield',
    Debtor_State: state,
    Debtor_Country: 'US',
    Debtor_Postal_Code: String(10000 + ((i * 97) % 80000)),
    Debtor_Email: `${first.toLowerCase()}.${last.toLowerCase()}@${company
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 12)}.demo`,
    Amount_Owed: amount,
    Status: status,
    Origin: origin,
    Entry_Tag: 'Demo',
    External_Invoice_ID: `DEMO-INV-${3000 + n}`,
    Invoice_ID: `DEMO-INV-${3000 + n}`,
    External_Account_Number: `ACCT-${88000 + n}`,
    Attorney_Name: attorney.name,
    Attorney_Email: attorney.email,
    Tracking_Number: statusIndex >= 2 ? trackingFor(n) : null,
    Dispute_Type: dispute,
    Dispute_Other: null,
    Evidence_Vault_URL: `catalyst://filestore/DemandVault/evidence_${id}.pdf`,
    Document_Vault_URL: statusIndex >= 2 ? `catalyst://filestore/DemandVault/demand_${id}.pdf` : null,
    Installment_Plan: plan.value,
    Payment_Structure: plan.paymentStructure,
    Payment_Term_ID: null,
    Settlement_Choice: 'NO',
    Attachments: [
      {
        id: `inv_${id}`,
        name: `invoice_${n}.pdf`,
        kind: 'Invoice',
        url: `catalyst://filestore/DemandVault/invoice_${id}.pdf`,
      },
    ],
    Jurisdiction_Clauses: `- Demo jurisdiction notes for ${state}.\n- Generated for portal styling and pipeline testing.`,
    Demand_Draft: `# DEMAND FOR PAYMENT\n\n**To:** ${first} ${last} / ${company}\n**Amount Due:** $${amount.toFixed(
      2
    )}\n\n## Notice\nDemo demand draft for ${state} testing.`,
    Created_At: isoDaysAgo(createdDays, 10 + (i % 8)),
    Updated_At: isoDaysAgo(updatedDays, 14 + (i % 6)),
  };
}

function buildMessagesForMatter(matter, index) {
  const statusIndex = STAGE_IDS.indexOf(matter.Status);
  if (statusIndex < 0) return [];
  const messages = [
    {
      ROWID: `msg_${matter.ROWID}_1`,
      Matter_ID: matter.ROWID,
      Sender_Role: 'SYSTEM',
      Sender_Name: 'DemandJet',
      Body: `Demo demand ${matter.ROWID} created (${matter.Entry_Tag}).`,
      Created_At: matter.Created_At,
    },
  ];
  if (statusIndex >= 1 && matter.Attorney_Name) {
    messages.push({
      ROWID: `msg_${matter.ROWID}_2`,
      Matter_ID: matter.ROWID,
      Sender_Role: 'ATTORNEY',
      Sender_Name: matter.Attorney_Name,
      Body: `I’ve been assigned on the ${matter.Debtor_Company} matter and am reviewing the package.`,
      Created_At: matter.Updated_At,
    });
  }
  if (statusIndex >= 2) {
    messages.push({
      ROWID: `msg_${matter.ROWID}_3`,
      Matter_ID: matter.ROWID,
      Sender_Role: 'CLIENT',
      Sender_Name: 'You',
      Body: 'Thanks — please proceed and keep me posted on delivery.',
      Created_At: matter.Updated_At,
    });
  }
  if (statusIndex >= 3) {
    messages.push({
      ROWID: `msg_${matter.ROWID}_4`,
      Matter_ID: matter.ROWID,
      Sender_Role: 'ATTORNEY',
      Sender_Name: matter.Attorney_Name || 'Attorney partner',
      Body:
        matter.Status === 'PAID'
          ? 'Payment confirmed. Closing out my side of the file.'
          : matter.Status === 'LITIGATION_READY'
            ? 'Deadline passed with no payment. Ready to discuss next steps.'
            : `Certified delivery confirmed for tracking ${matter.Tracking_Number}.`,
      Created_At: matter.Updated_At,
    });
  }
  // Keep message volume manageable: every other demand gets a thread preview.
  if (index % 2 === 1 && statusIndex === 0) return messages.slice(0, 1);
  return messages;
}

export function buildDemoSeedMatters(count = DEMO_SEED_COUNT) {
  return Array.from({ length: count }, (_, i) => buildMatter(i));
}

export function buildDemoSeedMessages(matters = buildDemoSeedMatters()) {
  const messages = {};
  matters.forEach((matter, index) => {
    messages[matter.ROWID] = buildMessagesForMatter(matter, index);
  });
  return messages;
}

export const demoSeedMatters = buildDemoSeedMatters();
export const demoSeedMessages = buildDemoSeedMessages(demoSeedMatters);

export function stageCounts(matters = demoSeedMatters) {
  return STAGE_IDS.reduce((acc, id) => {
    acc[id] = matters.filter((m) => m.Status === id).length;
    return acc;
  }, {});
}
